import React from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, Flag, AlertTriangle, X } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';

// Active-stage header controls: a Pause/Resume toggle (freezes all clocks via the backend) and a
// Finish button that completes the drill. Sized to match the old "Add Widget" header button.
export const ExerciseHeaderControls = ({
  incident,
  onRefresh,
  onFinish,
  canControlRuntime = true,
  canChangeStatus = true,
}) => {
  const [paused, setPaused] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!incident?.id) return;
    try {
      const res = await CommanServices.getExerciseLog(incident.id);
      if (res?.status) setPaused(!!res.data?.paused);
    } catch (_) {}
  }, [incident?.id]);

  React.useEffect(() => {
    load();
    const poll = setInterval(load, 8000);
    return () => clearInterval(poll);
  }, [load]);

  const handlePause = async () => {
    setBusy(true);
    try {
      const res = await CommanServices.toggleExercisePause(incident.id);
      if (res?.status) {
        setPaused(!!res.data?.paused);
        toast.success(res.data?.paused ? 'Exercise paused' : 'Exercise resumed');
        window.dispatchEvent(new Event('exercisePauseChanged'));
        onRefresh && onRefresh(true);
      }
    } finally { setBusy(false); }
  };

  const handleConfirmFinish = async () => {
    setShowFinishConfirm(false);
    if (onFinish) await onFinish();
  };

  return (
    <div className="flex-shrink-0 flex items-stretch gap-2">
      {canControlRuntime && <button
        data-testid={paused ? 'exercise-header-resume-btn' : 'exercise-header-pause-btn'}
        onClick={handlePause}
        disabled={busy}
        className={`flex items-center gap-2 rounded-xl px-5 shadow-md text-white text-sm font-bold tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-50 ${paused ? 'bg-emerald-600 hover:bg-emerald-500 ring-2 ring-emerald-300' : 'bg-amber-500 hover:bg-amber-400'}`}
        title={paused ? 'Resume the exercise clock' : 'Pause all timers (e.g. for a break)'}
      >
        {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        {paused ? 'Resume' : 'Pause'}
      </button>}
      {canChangeStatus && <button
        data-testid="exercise-header-finish-btn"
        onClick={() => setShowFinishConfirm(true)}
        disabled={busy}
        className="flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-5 shadow-md text-white text-sm font-bold tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-50"
        title="End the drill (mark as completed)"
      >
        <Flag className="h-4 w-4" /> Finish
      </button>}

      {canChangeStatus && showFinishConfirm && createPortal(
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          data-testid="finish-drill-confirm-overlay"
          onClick={() => setShowFinishConfirm(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="finish-drill-confirm-modal"
          >
            <button
              onClick={() => setShowFinishConfirm(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition-colors"
              title="Cancel"
              data-testid="finish-drill-cancel-x"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 ring-8 ring-amber-50">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div className="pt-0.5">
                  <h3 className="text-lg font-bold text-slate-900">Finish this drill now?</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    This ends the exercise and immediately <span className="font-semibold text-slate-700">stops all timers and counters</span>. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 rounded-b-2xl border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button
                onClick={() => setShowFinishConfirm(false)}
                disabled={busy}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200/70 transition-colors disabled:opacity-50"
                data-testid="finish-drill-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFinish}
                disabled={busy}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-emerald-500 transition-all active:scale-95 disabled:opacity-50"
                data-testid="finish-drill-confirm-btn"
              >
                <Flag className="h-4 w-4" /> Finish Drill
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExerciseHeaderControls;
